import "../../../assets/css/PremiumTools.css";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Hash,
  Copy,
  RefreshCw,
  Zap,
  Layers,
  CheckCircle,
  AlertCircle,
  Instagram,
  Youtube,
  Twitter,
  TrendingUp,
  Tag,
  ArrowLeft
} from "lucide-react";
import auth from "../../../firebase.init"; 
import toast from "react-hot-toast";

const SocialTagGenerator = () => {
  // ==========================================
  // 1. STATE MANAGEMENT
  // ==========================================
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // User Data & Credits
  const [credits, setCredits] = useState(0);
  const [apiKeys, setApiKeys] = useState([]);

  // Form State
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState("Instagram");
  const [tagCount, setTagCount] = useState(15);
  const [strategy, setStrategy] = useState("Mixed (Trending + Niche)");
  
  // Output State
  const [generatedTags, setGeneratedTags] = useState("");

  // Options
  const platforms = [
    { name: "Instagram", icon: <Instagram size={14} /> },
    { name: "TikTok", icon: <span style={{fontSize: 12, fontWeight: 800}}>Tk</span> }, // Lucide doesn't have TikTok yet
    { name: "YouTube", icon: <Youtube size={14} /> },
    { name: "X / Twitter", icon: <Twitter size={14} /> },
    { name: "LinkedIn", icon: <span style={{fontSize: 12, fontWeight: 800}}>In</span> }
  ];

  const strategies = [
    "Mixed (Trending + Niche)",
    "High Volume / Viral",
    "Niche / Specific",
    "Location Based",
    "SEO Focused"
  ];

  // ==========================================
  // 2. BACKEND / CREDIT LOGIC (UNCHANGED)
  // ==========================================
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        setApiKeys(response.data.data.map((keyObj) => keyObj.key));
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error(err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setLoading(false);
        setLoadingKeys(false);
        return;
      }
      try {
        await fetchApiKeys();
        const userResponse = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          setCredits(userResponse.data[0].currentBalance);
        }
      } catch (err) {
        setError("Failed to load user data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  const getRandomApiKey = () => {
    if (apiKeys.length === 0) throw new Error("No API keys available");
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // ==========================================
  // 3. AI GENERATION LOGIC
  // ==========================================

  const constructPrompt = () => {
    return `You are a Social Media Manager expert in SEO and viral growth.
    Generate a list of exactly ${tagCount} hashtags for ${platform}.
    
    Context/Topic: "${topic}"
    Strategy: ${strategy}

    Guidelines:
    1. Output ONLY the hashtags separated by spaces (e.g. #tag1 #tag2).
    2. Do NOT add bullet points, numbering, intro text, or explanations.
    3. Ensure tags are relevant and mix popular vs specific tags based on the strategy.
    4. If YouTube, output comma-separated tags instead of hashtags if the strategy implies video metadata. Otherwise use hashtags.`;
  };

  const generateTags = async () => {
    if (!topic.trim()) {
      toast.error("Please enter a topic or keyword.");
      return;
    }
    if (credits < 1) {
      setError("Insufficient credits.");
      return;
    }

    setGenerating(true);
    setError("");
    setGeneratedTags("");

    try {
      // 1. Deduct Credit
      const deductResponse = await axios.post(
        `https://allinoneai-d1a5a1b54c64.herokuapp.com/api/deduct-credit`,
        { userEmail: user.email, amount: 1 }
      );

      if (!deductResponse.data.success) throw new Error("Failed to deduct credits");
      setCredits((prev) => prev - 1);

      // 2. Call AI
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-lite:generateContent?key=${apiKey}`;
      
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: constructPrompt() }] }],
          generationConfig: { temperature: 0.7, maxOutputTokens: 1024 },
        }),
      });

      if (!response.ok) throw new Error("AI Generation Failed");

      const data = await response.json();
      const textResult = data.candidates?.[0]?.content?.parts?.[0]?.text;

      if (textResult) {
        setGeneratedTags(textResult.trim());
        toast.success("Tags generated!");
      } else {
        throw new Error("Empty response");
      }

    } catch (err) {
      console.error(err);
      toast.error(err.message);
      setError(err.message);

      // Refund Logic
      try {
        await axios.post(`https://allinoneai-d1a5a1b54c64.herokuapp.com/api/refund-credit`, {
          userEmail: user.email, amount: 1,
        });
        setCredits((prev) => prev + 1);
        toast("Credits refunded.");
      } catch (refundErr) { console.error(refundErr); }
    } finally {
      setGenerating(false);
    }
  };

  // ==========================================
  // 4. UTILS
  // ==========================================
  const copyToClipboard = () => {
    if (!generatedTags) return;
    navigator.clipboard.writeText(generatedTags).then(() => toast.success("Copied to clipboard!"));
  };

  // ==========================================
  // 5. RENDER UI
  // ==========================================

  if (loadingAuth || loading || loadingKeys) {
    return (
      <div className="d-flex justify-content-center align-items-center bg-dark" style={{ height: "100vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-wall d-flex align-items-center justify-content-center" style={{ height: "100vh", background: "#0f172a" }}>
        <div className="glass-card p-5 text-center" style={{ maxWidth: "400px", width: "90%" }}>
          <div className="mb-4 mx-auto bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center" style={{width: 60, height: 60}}>
            <Hash size={30} className="text-neon" />
          </div>
          <h2 className="fw-bold text-white mb-2">Tag Generator</h2>
          <p className="text-light opacity-75 mb-4">Please log in to access the tool.</p>
          <button className="btn btn-neon-primary btn-lg w-100 fw-semibold" onClick={() => window.location.href = "/login"}>
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="premium-app-container">
      {/* Background Orbs */}
      <div className="orb orb-1"></div>
      <div className="orb orb-2"></div>

      {/* Main Content */}
      <div className="container py-5 position-relative z-1">
        
        {/* Header Row */}
        <div className="d-flex justify-content-between align-items-center mb-5">
          <div className="d-flex align-items-center gap-3">
            <button className="btn btn-outline-light rounded-circle p-2 back-btn d-flex align-items-center justify-content-center" onClick={() => window.history.back()}>
              <ArrowLeft size={20} />
            </button>
            <h1 className="fw-bold m-0 text-white">
              Social Tags <span className="text-neon">Generator</span>
            </h1>
          </div>

          <div className="credits-badge">
            <Zap size={16} fill="#eab308" color="#eab308" />
            <span>{credits} Credits</span>
            {credits < 5 && (
              <button className="btn-buy-credits" onClick={() => window.location.href = "/user/select-package"}>
                Buy More
              </button>
            )}
          </div>
        </div>

        {error && <div className="alert alert-danger glass-alert">{error}</div>}

        <div className="row g-4">
          {/* LEFT SIDEBAR - FORM */}
          <div className="col-lg-4">
            <div className="glass-card h-100 d-flex flex-column">
              <div className="card-header-glass">
                <h5 className="m-0 d-flex align-items-center gap-2 text-white">
                  <Layers size={18} className="text-neon" />
                  Settings
                </h5>
              </div>

              <div className="card-body-glass flex-grow-1 d-flex flex-column overflow-auto">
                {/* Topic Input */}
                <div className="mb-4">
                  <label className="form-label text-light opacity-75">Post Topic / Keyword</label>
                  <textarea 
                    className="form-control input-group-glass" 
                    rows="3" 
                    placeholder="e.g. Sunset beach photography, Digital Marketing tips..."
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    style={{ resize: "none" }}
                  />
                </div>

                {/* Platform Selection */}
                <div className="mb-4">
                  <label className="form-label text-light opacity-75">Platform</label>
                  <div className="d-flex flex-wrap gap-2">
                    {platforms.map((p) => (
                      <div 
                        key={p.name} 
                        className={`btn ${platform === p.name ? 'btn-neon-primary' : 'btn-outline-light opacity-75'} d-flex align-items-center gap-2 flex-grow-1 justify-content-center`}
                        style={{ padding: '8px 12px' }}
                        onClick={() => setPlatform(p.name)}
                      >
                        {p.icon}
                        <span style={{ fontSize: '0.85rem' }}>{p.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Strategy Dropdown */}
                <div className="mb-4">
                  <label className="form-label text-light opacity-75">Strategy</label>
                  <div className="position-relative">
                     <span className="position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', zIndex: 10 }}>
                        <TrendingUp size={16} />
                     </span>
                     <select 
                      className="form-select input-group-glass ps-5"
                      value={strategy}
                      onChange={(e) => setStrategy(e.target.value)}
                    >
                      {strategies.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                {/* Count Slider */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label text-light opacity-75 m-0">Tag Count</label>
                    <span className="badge bg-dark border border-secondary">{tagCount}</span>
                  </div>
                  <input 
                    type="range" 
                    className="form-range" 
                    min="5" 
                    max="30" 
                    step="5" 
                    value={tagCount}
                    onChange={(e) => setTagCount(Number(e.target.value))}
                  />
                  <div className="d-flex justify-content-between text-light opacity-50 small">
                    <span>5</span><span>30</span>
                  </div>
                </div>

                {/* Generate Button */}
                <div className="mt-auto pt-3">
                  <button 
                    className="btn btn-neon-primary w-100 d-flex align-items-center justify-content-center gap-2"
                    onClick={generateTags}
                    disabled={generating || credits < 1 || !topic.trim()}
                  >
                    {generating ? (
                      <>
                        <RefreshCw className="spin" size={18} /> Analyzing...
                      </>
                    ) : (
                      <>
                        <Zap size={18} /> Generate Tags (1 Credit)
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDEBAR - OUTPUT */}
          <div className="col-lg-8">
            <div className="glass-card h-100 d-flex flex-column">
              <div className="card-header-glass d-flex justify-content-between align-items-center">
                <h5 className="m-0 d-flex align-items-center gap-2 text-white">
                  <Tag size={18} className={generatedTags ? "text-success" : "text-neon"} />
                  Generated Tags
                </h5>
                {generatedTags && (
                  <button className="btn btn-sm btn-outline-light d-flex align-items-center gap-2" onClick={copyToClipboard}>
                    <Copy size={14} /> Copy All
                  </button>
                )}
              </div>

              <div className="card-body-glass flex-grow-1 overflow-auto position-relative" style={{ minHeight: "500px" }}>
                {generating ? (
                  <div className="h-100 d-flex flex-column align-items-center justify-content-center text-light opacity-75 py-5">
                    <div className="spinner-border text-neon mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5 className="text-white">Curating Hashtags...</h5>
                    <p className="small">Checking trends for {platform}</p>
                  </div>
                ) : generatedTags ? (
                  <div className="d-flex flex-column h-100">
                    <div className="d-flex flex-wrap gap-2 mb-4 p-2">
                      {generatedTags.split(' ').map((tag, idx) => (
                        tag.trim() && (
                          <span key={idx} className="badge rounded-pill bg-light text-dark bg-opacity-75 px-3 py-2 fw-medium border border-white border-opacity-25" style={{ fontSize: '1rem' }}>
                            {tag}
                          </span>
                        )
                      ))}
                    </div>
                    <div className="mt-auto border-top border-secondary border-opacity-25 pt-3 d-flex justify-content-between align-items-center">
                      <span className="text-success d-flex align-items-center gap-1 small fw-medium">
                        <CheckCircle size={14} /> Optimized for {platform}
                      </span>
                      <span className="text-light opacity-50 small">
                        {generatedTags.split(' ').filter(t => t.trim()).length} Tags
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="h-100 d-flex flex-column align-items-center justify-content-center text-light opacity-25 py-5">
                    <Hash size={64} className="mb-3" />
                    <h3 className="fs-4 mb-2 text-white opacity-50">No Tags Generated Yet</h3>
                    <p className="text-center">Select your platform and topic to start.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      
    </div>
  );
};

export default SocialTagGenerator;
